import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { ghostBtnCls, inputCls, primaryBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const rup = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const PERIODS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "Last week"], ["month", "This month"], ["custom", "Custom"]];

export default function LabReferralBusiness() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ from_date: "", to_date: "" });

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/lab/business/referral", { params: { period, from_date: range.from_date || undefined, to_date: range.to_date || undefined } }); setData(data); }
    catch (e) { setError(apiError(e)); }
  }, [period, range]);
  useEffect(() => { if (period !== "custom") load(); /* eslint-disable-next-line */ }, [period]);

  const downloadCsv = () => {
    if (!data) return;
    const rows = [["Referrer", "Cases", "Business", "Collected"], ...data.rows.map((r) => [r.referrer_name, r.cases, r.business, r.collected])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "referral_business.csv"; a.click();
  };

  if (!can("lab.reports.view")) return <NoAccess what="referral business" />;

  return (
    <div data-testid="lab-referral-business-page">
      <PageHeader overline="Business" title="Referral Business" subtitle="Business attributed to each referring doctor over the chosen period."
        actions={<button className={ghostBtnCls} onClick={downloadCsv} data-testid="ref-download">Download</button>} />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="ref-periods">
        {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} data-testid={`ref-period-${k}`} className={`px-4 py-2 rounded-full text-sm ${period === k ? "bg-black text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>
      {period === "custom" && (
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap gap-3 mb-5">
          <input type="date" className={inputCls} value={range.from_date} onChange={(e) => setRange({ ...range, from_date: e.target.value })} data-testid="ref-from" />
          <input type="date" className={inputCls} value={range.to_date} onChange={(e) => setRange({ ...range, to_date: e.target.value })} data-testid="ref-to" />
          <button className={primaryBtnCls} data-testid="ref-generate">Generate</button>
        </form>
      )}
      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : (
        <>
          <div className="flex gap-3 mb-4 text-sm"><div className="panel px-4 py-2">Cases: <b>{data.total_cases}</b></div><div className="panel px-4 py-2">Business: <b>{rup(data.total_business)}</b></div><div className="panel px-4 py-2 text-slate-500">{data.from} → {data.to}</div></div>
          <Table testid="lab-referral-table" columns={["Referrer", "Cases", "Business", "Collected"]}
            rows={data.rows} empty={<EmptyState title="No referral business" subtitle="No cases in this period." testid="lab-referral-empty" />}
            renderRow={(r) => (
              <tr key={r.referrer_id} data-testid={`ref-row-${r.referrer_id}`}>
                <Td className="font-medium text-slate-900">{r.referrer_name}</Td>
                <Td className="text-slate-700">{r.cases}</Td><Td className="text-slate-700">{rup(r.business)}</Td><Td className="text-emerald-700">{rup(r.collected)}</Td>
              </tr>
            )} />
        </>
      )}
    </div>
  );
}
